import React from "react";

/**
 * Price — canonical way to render a plan/service/module price across VenturaZ.
 *
 * When only `price` is passed, it renders a single figure. When `originalPrice`
 * is also passed and greater than `price`, the original is struck through and
 * the discounted price is rendered LARGER + in gold, with the savings % badge
 * as a small pill.
 */
const inrFmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export function Price({
  price,
  originalPrice,
  size = "md",         // 'sm' | 'md' | 'lg' | 'xl'
  suffix,               // e.g. "/ year", "one-time", "per report"
  align = "left",
  className = "",
  testId,
}) {
  const hasDiscount = originalPrice != null && Number(originalPrice) > Number(price);
  const savingsPct = hasDiscount
    ? Math.round(((Number(originalPrice) - Number(price)) / Number(originalPrice)) * 100)
    : 0;

  const sizeStyles = {
    sm: { discounted: "text-lg", original: "text-xs", suffix: "text-[10px]" },
    md: { discounted: "text-2xl lg:text-3xl", original: "text-sm", suffix: "text-[11px]" },
    lg: { discounted: "text-3xl lg:text-4xl", original: "text-base", suffix: "text-xs" },
    xl: { discounted: "text-4xl lg:text-5xl", original: "text-lg", suffix: "text-sm" },
  }[size] || { discounted: "text-2xl", original: "text-sm", suffix: "text-[11px]" };

  const alignCls = align === "right" ? "items-end text-right" : align === "center" ? "items-center text-center" : "items-start text-left";

  return (
    <div className={`flex flex-col ${alignCls} ${className}`} data-testid={testId}>
      {hasDiscount && (
        <div className="flex items-baseline gap-2">
          <span className={`${sizeStyles.original} font-mono text-ink/45 line-through`} data-testid="price-original">
            {inrFmt(originalPrice)}
          </span>
          <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-mono uppercase tracking-widest">
            {savingsPct}% off
          </span>
        </div>
      )}
      <div className="flex items-baseline gap-1.5">
        <span
          className={`${sizeStyles.discounted} font-display leading-none ${hasDiscount ? "text-gold" : "text-navy-deep"}`}
          data-testid="price-current"
        >
          {inrFmt(price)}
        </span>
        {suffix && (
          <span className={`${sizeStyles.suffix} font-mono text-ink/55 uppercase tracking-wider`}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

export default Price;
